/* eslint-disable no-param-reassign */
import ko from 'knockout';
import { isArray, each, bind, isEqual } from 'underscore';
import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { Toggle, Text } from '@fluentui/react';
import { cloneDeep } from '@bingads-webui-universal/primitive-utilities';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import AutoTargetView from '@bingads-webui-campaign-legacy/auto-targets/view/create/index';
import { dsaCampaignStyles, creationCommonStyle, campaignSettingsStyles, adgroupsKeywordsStyles } from '@bingads-webui-campaign-react/campaign-adgroup-settings-shared-styles';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';

export const getStyles = (_, { palette = {} }) => (
  Object.assign(
    {},
    {
      finalUrlContainer: Object.assign(
        {},
        creationCommonStyle({ palette }),
        dsaCampaignStyles({ palette }),
        campaignSettingsStyles({ palette }),
        adgroupsKeywordsStyles({ palette })
      ),
    }
  )
);

class LegacyFinalUrlExpansionUnStyled extends React.Component {
  static propTypes = {
    data: PropTypes.shape({
      length: PropTypes.number,
    }).isRequired,
    onChange: PropTypes.func.isRequired,
    setFinalUrlExpansionsView: PropTypes.func.isRequired,
    isFinalUrlExpansionEnabled: PropTypes.bool.isRequired,
    i18n: PropTypes.shape({
      getString: PropTypes.func,
    }).isRequired,
    classes: PropTypes.shape({
      finalUrlContainer: PropTypes.string,
    }).isRequired,
  };
  constructor(props) {
    super(props);

    const { isFinalUrlExpansionEnabled } = props;
    this.state = { isFinalUrlExpansionEnabled };
    this.view = React.createRef();
    this.container = null;
    this.operatorMap = {
      Equals: this.props.i18n.getString(_TL_('equals')),
      Contains: this.props.i18n.getString(_TL_('contains')),
    };

    this.operandMap = {
      Url: this.props.i18n.getString(_TL_('URL')),
    };
  }

  componentDidMount() {
    this.setViewAndUpdateViewModel();
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps.data, this.props.data) || prevProps.isFinalUrlExpansionEnabled !== this.props.isFinalUrlExpansionEnabled) {
      this.setViewAndUpdateViewModel();
    }
    // We will refresh the view if data changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }

  setViewAndUpdateViewModel() {
    if (this.state.isFinalUrlExpansionEnabled) {
      if (!this.view.current) {
        this.view.current = new AutoTargetView({
          isForVNext: true,
          onChange: bind((newData) => {
            this.props.onChange({
              finalUrlExpansionOptOut: !this.state.isFinalUrlExpansionEnabled,
              finalUrlExpansionsBulkUpsert: newData,
            });
          }),
          isNegativeAutoTarget: true,
          isShowUrlOnly: true,
          selectedItem: 'mixedspecificwebpage',
          hideViewSelection: true,
          hideWebsiteLabel: true,
          isExclude: true,
        });
      }

      this.view.current.render();
      this.view.current.$el.appendTo(this.container);
      this.props.setFinalUrlExpansionsView(this.view);

      if (isArray(this.props.data)) {
        let toAddToViewModel = [];
        this.view.current.updateViewModels(cloneDeep(this.props.data));
        each(this.props.data, (target) => {
          let isExactMatch = false;
          const conditions = target.Criterion.WebPage.Parameter.Conditions;
          const results = [];

          each(conditions, (condition) => {
            if (condition.Operator === 'Equals') {
              isExactMatch = true;
              results.push({
                conditions: [{
                  Operand: condition.Operand,
                  ArgumentOperand: condition.ArgumentOperand,
                  Operator: condition.Operator,
                  DisplayOperator: this.operatorMap[condition.Operator],
                  DisplayOperand: this.operandMap[condition.Operand],
                },
                ],
                type: 'exacturls',
              });
            } else {
              results.push({
                Operand: ko.observable(condition.Operand),
                ArgumentOperand: ko.observable(condition.ArgumentOperand),
                Operator: ko.observable(condition.Operator),
                DisplayOperator: ko.observable(this.operatorMap[condition.Operator]),
                DisplayOperand: ko.observable(this.operandMap[condition.Operand]),
              });
            }
          });

          if (isExactMatch) {
            toAddToViewModel = toAddToViewModel.concat(results);
          } else {
            toAddToViewModel.push({
              conditions: results,
              type: 'specificpage',
              shouldCheckArgumentOperandFormat: true,
            });
          }
        });
        if (this.props.data.length > 0) {
          this.view.current.selectedTargetsView.viewmodel.addBatch(toAddToViewModel);
        }
      }

      return () => {
        this.view.current.remove();
      };
    }
    return null;
  }

  static i8n = I18nContext;

  render() {
    const onToggle = (event, checked) => {
      this.setState({
        isFinalUrlExpansionEnabled: checked,
      }, () => {
        if (!checked) {
          this.view.current.selectedTargetsView.viewmodel.clear();
        } else {
          this.props.onChange({
            finalUrlExpansionOptOut: false,
          });
        }
      });
    };

    return (
      <div
        className={classnames(this.props.classes.finalUrlContainer)}
      >
        <Toggle
          checked={this.state.isFinalUrlExpansionEnabled}
          onText={this.props.i18n.getString(_TL_('On: You’ve enabled Final URL expansion'))}
          offText={this.props.i18n.getString(_TL_('Off: Use the Final URL only'))}
          onChange={onToggle}
          role="checkbox"
        />
        {this.state.isFinalUrlExpansionEnabled &&
        <div className="final-url-container">
          <div>
            <Text>
              {this.props.i18n.getString(_TL_('You have the option to exclude certain webpages from being displayed in your ads. Please note that these URLs won’t appear in your ads even if you’ve enabled Final URL expansion. The exclusion list requires 24 hours for it to become fully effective.'))}
            </Text>
          </div>
          <div ref={(ref) => { this.container = ref; }} />
        </div>
        }
      </div>
    );
  }
}

export const LegacyFinalUrlExpansion = withDefaultStyles(LegacyFinalUrlExpansionUnStyled, getStyles);
